import { hasDefinedValue, isDefined } from '@sgme/fp';
import type { CellEditRequestEvent, ColDef, ColTypeDef, GetRowIdParams, RowGroupOpenedEvent } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import { ProductGreeksCellRenderer } from 'components/secondaryMarket/productSearch/ProductGreeksCellRenderer';
import { useOpenLifecycleFromProductId } from 'components/secondaryMarket/productSearch/utils';
import { DetailCellRendererFactory } from 'components/shared/Blotter/DetailCellRendererFactory';
import { technicalColumnDefinition } from 'components/shared/Blotter/Grid/blotter.model';
import { ClientSideBlotterGrid } from 'components/shared/Blotter/Grid/client-side-blotter';
import { useAgGridApiState } from 'components/shared/Blotter/Grid/Hooks/useApiState';
import { useClientSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useClientSideDefaultColumnTypes';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useDefaultGroupColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnGroupDefinition';
import { useEditableClientColumnType } from 'components/shared/Blotter/Grid/Hooks/useEditableClientColumnType';
import { useEditableCustodianColumnType } from 'components/shared/Blotter/Grid/Hooks/useEditableCustodianColumnType';
import {
  createMasterDetailTechnicalColumn,
  defaultMasterDetailWidth,
} from 'components/shared/Blotter/Grid/Hooks/useMasterDetailTechnicalColumn';
import { useScrollOnSearchDone } from 'components/shared/Blotter/Grid/Hooks/useScrollOnItemsAdded';
import { useStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import type { IntlKey } from 'locales';
import { type PropsWithChildren, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { SecondaryMarketProduct, SecondaryMarketProductInternalUser } from 'state/secondaryMarket/productSearch';
import {
  getRowIdFromSecondaryMarketProduct,
  hasTradeAuthorisation,
  hasTradeAuthorisationFor,
} from 'state/secondaryMarket/productSearch/productSearch.utils';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useDebounceEventCntHandler } from '../../../../hooks/useDebounceEventCntHandler';
import {
  getIsRefreshing,
  getIsRefreshingByRowId,
} from '../../../../state/secondaryMarket/productSearch/productSearch.selectors';
import type {
  IconActionCellRendererProps,
  IconActionFloatingFilterProps,
} from '../../../shared/Blotter/Grid/CellRenderers/IconActionCellRenderer';
import {
  buildIconAction,
  buildIconActionFloatingFilter,
  IconActionCellRenderer,
  IconActionFloatingFilterComponent,
} from '../../../shared/Blotter/Grid/CellRenderers/IconActionCellRenderer';
import { useEditableStringColumnType } from '../../../shared/Blotter/Grid/Hooks/useEditableStringColumnType';
import { usePercentOrAmountColumnType } from '../../../shared/Blotter/Grid/Hooks/usePercentOrAmountColumnType';
import { ConnectedProductSummaryModal } from '../modal';
import { processCellForClipboard, useProcessCellFromClipboard } from './clipboard';
import { useBidAskColumnType, useHasDlpSecondaryColumn, useLimitOrderStatusColumnType, useModalState } from './hooks';
import type { ResultGridsProps } from './ResultsGrid.connect';

const getHeaderIntlKey = (key: string): IntlKey => `secondaryMarket.products.column.header.${key}` as any;

function getTradeAuthorisationColor(value: string | undefined) {
  return hasTradeAuthorisation(value) ? 'success' : 'danger';
}

export function getLiveStatusColor(value: string | undefined | null) {
  switch (value?.toUpperCase()) {
    case 'ON':
      return 'success';
    case 'OFF':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function RefreshIconActionCellRenderer({ data, ...props }: PropsWithChildren<IconActionCellRendererProps>) {
  const isRefreshing = useAppSelector(s => getIsRefreshingByRowId(s, data!.rowId));
  return <IconActionCellRenderer data={data} isBusy={isRefreshing} {...props} />;
}

export function RefreshActionFloatingFilterComponent(props: PropsWithChildren<IconActionFloatingFilterProps>) {
  const isRefreshing = useAppSelector(s => getIsRefreshing(s));
  return <IconActionFloatingFilterComponent {...props} isBusy={isRefreshing} />;
}

const detailCellRendererParams = { refreshStrategy: 'everything' };
const detailCellRenderer = DetailCellRendererFactory(ProductGreeksCellRenderer, {
  paddingLeft: defaultMasterDetailWidth * 2,
});

const hasClosePriceGreeks = hasDefinedValue('closePriceGreeks');
const hasLiveGreeks = hasDefinedValue('liveGreeks');

function isRowMaster(product: SecondaryMarketProductInternalUser) {
  return (
    (hasClosePriceGreeks(product) && product.closePriceGreeks.length > 0) ||
    (hasLiveGreeks(product) && product.liveGreeks.length > 0)
  );
}

const masterDetailColumn = createMasterDetailTechnicalColumn({ initialPinned: 'left' });

const hasNotDlp = (data: SecondaryMarketProduct) => data.hasDlpSecondaryMarket !== true;

const agGridModules = [MasterDetailModule];

export function ResultsGrid({
  columns,
  isSearchingProducts,
  items,
  pageSizeState,
  columnState,
  columnGroupState,
  onBlotterColumnStateChanged,
  onRemoveProduct,
  onRemoveAll,
  onSelectProduct,
  onValuesChangedRequest,
  onOpenCPOrderTab,
  onOpenLiveTab,
  isCpOrderEntitled,
  isLiveQuoteEntitled,
  onRefreshProduct,
  onRefreshAll,
  onFetchProductUnderlyings,
  onOpenLimitOrderTab,
  onOpenDlpProduct,
  onProductFocused,
}: ResultGridsProps) {
  const [gridApi, onGridReady] = useAgGridApiState<SecondaryMarketProduct>();
  useScrollOnSearchDone(isSearchingProducts, gridApi);
  const [{ hasSelection, selectedRow }, { onSelectedRow, onUnselect }] = useModalState();
  const isInternal = useIsInternalUser();
  const onClickSelectProduct = useCallback(
    (data: SecondaryMarketProduct) => {
      if (data.hasDlpSecondaryMarket && data.isin) {
        onOpenDlpProduct(data.isin);
      } else {
        onSelectedRow(data);
        onSelectProduct({ isin: data.isin });
      }
    },
    [onSelectProduct, onSelectedRow, onOpenDlpProduct],
  );

  const onRefreshProductDebounced = useDebounceEventCntHandler(onRefreshProduct, 250);
  const onCellEditRequest = useCallback(
    (event: CellEditRequestEvent) => {
      const rowId = event.data.rowId;
      const field = event.colDef.field! as keyof SecondaryMarketProduct;
      const value = event.value;
      const isAsync = field === 'salesMessage';
      onValuesChangedRequest({ rowId, field, value, isAsync });
    },
    [onValuesChangedRequest],
  );

  const { formatMessage } = useIntl();

  const getPriceInfo = useCallback((data: any) => {
    return { priceType: data.priceType, currency: data.currency };
  }, []);

  const defaultColumnDef = useDefaultColumnDefinition<SecondaryMarketProduct>(getHeaderIntlKey);
  const defaultGroupColumnDef = useDefaultGroupColumnDefinition(getHeaderIntlKey);
  const percentOrAmountColumn = usePercentOrAmountColumnType(getPriceInfo);
  const cpOrderRedirectEnabled = useCallback(
    (data: SecondaryMarketProductInternalUser, type: 'bid' | 'ask') =>
      isCpOrderEntitled && hasTradeAuthorisationFor(data.cpTradeAuthorisation, type),
    [isCpOrderEntitled],
  );
  const cpBidAskColumn = useBidAskColumnType(getPriceInfo, cpOrderRedirectEnabled, onOpenCPOrderTab);
  const liveRedirectEnabled = useCallback(
    (data: SecondaryMarketProductInternalUser, type: 'bid' | 'ask') =>
      isLiveQuoteEntitled && hasTradeAuthorisationFor(data.rtTradeAuthorisation, type),
    [isLiveQuoteEntitled],
  );
  const rtBidAskColumn = useBidAskColumnType(getPriceInfo, liveRedirectEnabled, onOpenLiveTab);

  const tradeAuthorisationColumn = useStatusColumnType(getTradeAuthorisationColor);
  const liveStatusColumn = useStatusColumnType(getLiveStatusColor);
  const limitOrderStatusColumn = useLimitOrderStatusColumnType(onOpenLimitOrderTab);
  const salesMessage = useEditableStringColumnType(true);
  const custodian = useEditableCustodianColumnType();
  const client = useEditableClientColumnType();
  const openLifecycleFromProductId = useOpenLifecycleFromProductId();

  const refreshColumn = useMemo(
    () =>
      ({
        ...technicalColumnDefinition,
        ...buildIconAction({
          onClick: (data: SecondaryMarketProduct) =>
            onRefreshProductDebounced({ bcsKey: data.rowId, dlpKey: data.isin ? `ISIN::${data.isin}` : undefined }),
          icon: 'sync',
          title: formatMessage({ id: 'blotter.action.refreshItem' }),
          e2e: 'refresh-product',
          cellRenderer: RefreshIconActionCellRenderer,
        }),
        ...buildIconActionFloatingFilter({
          onClick: onRefreshAll,
          icon: 'sync',
          title: formatMessage({ id: 'blotter.action.refreshAll' }),
          e2e: 'refresh-all-products',
          floatingFilterComponent: RefreshActionFloatingFilterComponent,
        }),
        pinned: 'right',
        cellStyle: { padding: 0 },
      }) as ColDef,
    [formatMessage, onRefreshAll, onRefreshProductDebounced],
  );

  const browseColumn = useMemo(
    () =>
      ({
        ...technicalColumnDefinition,
        ...buildIconAction<SecondaryMarketProduct>({
          onClick: product => {
            openLifecycleFromProductId(product.rowId);
          },
          icon: 'launch',
          title: formatMessage({ id: 'secondaryMarket.modal.buttonOpenInLifecycle' }),
          e2e: 'open-lifecycle',
          getIsVisible: hasNotDlp,
        }),
        flex: 0,
        pinned: 'right',
        cellStyle: { padding: 0 },
      }) as ColDef,
    [formatMessage, openLifecycleFromProductId],
  );

  const removeColumn = useMemo(
    () =>
      ({
        ...technicalColumnDefinition,
        ...buildIconAction({
          onClick: (data: SecondaryMarketProduct) => {
            onRemoveProduct({ rowId: data.rowId, fileCode: data.fileCode!, isin: data.isin! });
          },
          icon: 'delete',
          title: formatMessage({ id: 'blotter.action.removeItem' }),
          e2e: 'clear-product',
        }),
        ...buildIconActionFloatingFilter({
          onClick: onRemoveAll,
          icon: 'delete_sweep',
          title: formatMessage({ id: 'blotter.action.clearAll' }),
          e2e: 'clear-all-products',
        }),
        width: 40,
        pinned: 'right',
        cellStyle: { padding: 0 },
      }) as ColDef,
    [formatMessage, onRemoveAll, onRemoveProduct],
  );

  const hasDlpSecondaryColumn = useHasDlpSecondaryColumn(onOpenDlpProduct);

  const arrowColumn = useMemo(
    () =>
      ({
        ...technicalColumnDefinition,
        ...buildIconAction({
          onClick: onClickSelectProduct,
          icon: 'arrow_forward',
          title: formatMessage({ id: 'blotter.action.selectProduct' }),
          e2e: 'select-product',
        }),
        width: 40,
        flex: 0,
        pinned: 'right',
        cellStyle: { padding: 0 },
      }) as ColDef,
    [formatMessage, onClickSelectProduct],
  );

  const customColumns: Record<string, ColTypeDef> = useMemo(
    () => ({
      custodian,
      client,
      salesMessage: salesMessage,
      percentOrAmount: percentOrAmountColumn,
      cpTradeAuthorisation: tradeAuthorisationColumn,
      rtTradeAuthorisation: tradeAuthorisationColumn,
      rtBidAsk: rtBidAskColumn,
      cpBidAsk: cpBidAskColumn,
      liveStatus: liveStatusColumn,
      limitOrderStatus: limitOrderStatusColumn,
      stringWithMasterDetail: masterDetailColumn,
      refresh: refreshColumn,
      browse: browseColumn,
      remove: removeColumn,
      arrow: arrowColumn,
      hasDlpSecondaryMarket: hasDlpSecondaryColumn,
    }),
    [
      arrowColumn,
      browseColumn,
      client,
      cpBidAskColumn,
      custodian,
      limitOrderStatusColumn,
      liveStatusColumn,
      percentOrAmountColumn,
      refreshColumn,
      removeColumn,
      rtBidAskColumn,
      salesMessage,
      tradeAuthorisationColumn,
      hasDlpSecondaryColumn,
    ],
  );

  const columnTypes = useClientSideDefaultColumnTypes(customColumns);

  const getRowId = useCallback(({ data }: GetRowIdParams<SecondaryMarketProduct>) => {
    return isDefined(data) ? getRowIdFromSecondaryMarketProduct(data) : '';
  }, []);

  const onRowGroupOpened = useCallback(
    (e: RowGroupOpenedEvent<SecondaryMarketProduct>) => {
      if (e.expanded && !!e.data?.rowId) {
        logAnalytics.trackEvent('Secondary market', 'Open greeks');
        onFetchProductUnderlyings(e.data?.rowId);
      }
    },
    [onFetchProductUnderlyings],
  );

  const processCellFromClipboard = useProcessCellFromClipboard(getRowIdFromSecondaryMarketProduct);

  if (columns === null || items === null) {
    return null;
  }

  return (
    <>
      <ClientSideBlotterGrid<SecondaryMarketProduct>
        onGridReady={onGridReady}
        detailCellRendererParams={detailCellRendererParams}
        columnDefinitions={columns}
        rowData={items as SecondaryMarketProduct[]}
        animateRows
        suppressRowDrag
        enableFillHandle
        onRowGroupOpened={onRowGroupOpened}
        fillHandleDirection="y"
        defaultColDef={defaultColumnDef}
        defaultColGroupDef={defaultGroupColumnDef}
        suppressRowClickSelection
        isRowMaster={isRowMaster}
        getRowId={getRowId}
        columnTypes={columnTypes}
        masterDetail
        keepDetailRows
        detailRowAutoHeight
        detailCellRenderer={detailCellRenderer}
        modules={agGridModules}
        e2e={''}
        logAnalyticName="Secondary market"
        name="secondaryMarket.products"
        pagination={false}
        pageSizeState={pageSizeState}
        columnState={columnState}
        columnGroupState={columnGroupState}
        onBlotterColumnStateChanged={onBlotterColumnStateChanged}
        readOnlyEdit
        onCellEditRequest={onCellEditRequest}
        processCellForClipboard={processCellForClipboard}
        processCellFromClipboard={processCellFromClipboard}
        onRangeSelectionChanged={e => {
          // Focus a product row only on mono row selection
          const ranges = e.api.getCellRanges();
          if (ranges?.some(r => r.endRow !== r.startRow)) {
            onProductFocused(undefined);
          } else {
            const index = ranges?.at(0)?.startRow?.rowIndex;
            if (isDefined(index)) {
              onProductFocused(e.api.getDisplayedRowAtIndex(index)?.id);
            }
          }
        }}
      />
      <ConnectedProductSummaryModal
        isOpen={hasSelection && isInternal}
        onClose={onUnselect}
        onToggleOpen={onUnselect}
        data={selectedRow}
      />
    </>
  );
}
